import { Divider, Stack } from '@mui/material';
import React, { Dispatch, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fullSize } from '../../../Constants';
import useBreakpoint from '../../../hooks/useBreakpoint';
import { getVulnerabilities } from '../../../redux/vulnerabilities/sliceVulnerabilities';
import { IAppReduxStore } from '../../../redux/store';
import VulnerabilityList from './VulnerabilityList/VulnerabilityList';
import VulnerabilityViewSidebar from './VulnerabilityList/VulnerabilityViewSidebar';
import Footer from '../../General/Footer/Footer';
import Header from '../../General/Header/Header';
import Loading from '../../General/Loading/Loading';
import { VulnerabilityViewControls } from './VulnerabilityViewControls';

/**
 * Application view that allows a user to browse their vulnerability databases and open then in the VulnerabilityView.
 */
export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'vulnerabilitySelectionView' });

    /** Detect screen size. */
    const { isDesktop } = useBreakpoint();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Access relevant state variables from the redux store. */
    const vulnerabilitiesLoading = useSelector(
        (store: IAppReduxStore) => store.vulnerabilities.loading,
    );

    /**
     * Fetches the user's vulnerability list when the component first renders.
     */
    useEffect(() => {
        dispatch(getVulnerabilities());
    }, []);

    if (vulnerabilitiesLoading) {
        return <Loading />;
    }

    /** Content component. */
    const content = (
        <Stack
            direction='column'
            sx={{
                flexGrow: 4,
            }}
        >
            <VulnerabilityViewControls />

            <Stack
                direction='row'
                sx={{
                    flexGrow: 4,
                    position: 'relative',
                }}
            >
                <VulnerabilityList />
            </Stack>
        </Stack>
    );

    return (
        <Stack
            direction='column'
            sx={{
                flexGrow: 4,
                ...fullSize,
            }}
        >
            <Header />

            {isDesktop ? (
                content
            ) : (
                <Stack
                    direction='row'
                    sx={{
                        flexGrow: 4,
                    }}
                >
                    <VulnerabilityViewSidebar />

                    <Divider orientation='vertical' flexItem />

                    {content}
                </Stack>
            )}

            <Footer />
        </Stack>
    );
};
